/* eslint-disable no-param-reassign -- each counter item is this hook's own mutable animation record, not caller state */
import type { RefObject } from 'react';
import { useEffect } from 'react';

import { prefersReducedMotion } from './use-page-motion';

interface CounterItem {
  el: HTMLElement;
  box: HTMLElement;
  final: string;
  to: number;
  decimals: number;
  meter: HTMLElement | null;
}

type GroupState = 'final' | 'zero' | 'running' | 'done';

const DURATION = 1800;
const STAGGER = 120;

/**
 * The source's counters: every `[data-count-group]` inside `rootRef` counts its `[data-count-to]` figures
 * from 0 each time it enters the viewport (expo-out, staggered), and shows the final strings again once
 * well out of sight. The box of each figure is fixed at the width of its final string (in em, so it
 * follows the clamp()ed type); a string a little wider than the box ("3.000.000" against "3 milhões") is
 * squeezed with scaleX, so the "+", the "%" and every neighbour never move. The map meters grow in the
 * same frame. Screen readers only ever read the sr-only finals next to these aria-hidden figures.
 */
export function useCounters(rootRef: RefObject<HTMLElement>, locale: string): void {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) return undefined;
    const format = (value: number, decimals: number) => {
      try {
        return new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
      } catch {
        return decimals ? value.toFixed(decimals) : String(value);
      }
    };
    const teardowns: (() => void)[] = [];

    root.querySelectorAll<HTMLElement>('[data-count-group]').forEach((group) => {
      const items: CounterItem[] = Array.from(group.querySelectorAll<HTMLElement>('[data-count-to]')).map((element) => {
        const stat = element.closest('.stat');
        return {
          el: element,
          box: element.closest<HTMLElement>('.cntbox') ?? element,
          final: element.textContent ?? '',
          to: parseFloat(element.getAttribute('data-count-to') ?? '0'),
          decimals: parseInt(element.getAttribute('data-count-dec') ?? '0', 10),
          meter: stat ? stat.querySelector<HTMLElement>('.stat__meter i') : null,
        };
      });
      let frame = 0;
      let state: GroupState = 'final';
      let inView = false;

      const squeeze = (item: CounterItem, end: boolean) => {
        const width = item.box.clientWidth;
        const needed = item.box.scrollWidth;
        item.box.style.transform = !end && width && needed > width + 0.5 ? `scaleX(${(width / needed).toFixed(4)})` : '';
      };
      const reserve = () => {
        items.forEach((item) => {
          const current = item.el.textContent ?? '';
          const fontSize = parseFloat(getComputedStyle(item.box).fontSize) || 16;
          item.box.style.width = '';
          item.box.style.transform = '';
          item.el.textContent = item.final;
          item.box.style.width = `${(item.box.getBoundingClientRect().width / fontSize).toFixed(4)}em`;
          item.el.textContent = current;
          squeeze(item, current === item.final);
        });
      };
      const paint = (item: CounterItem, eased: number, end: boolean) => {
        const value = item.decimals ? Math.round(item.to * eased * 10 ** item.decimals) / 10 ** item.decimals : Math.round(item.to * eased);
        const text = end ? item.final : format(value, item.decimals);
        if (item.el.textContent !== text) {
          item.el.textContent = text;
          squeeze(item, end);
        }
        if (item.meter) item.meter.style.transform = `scaleX(${eased.toFixed(4)})`;
      };
      const reset = () => {
        reserve();
        window.cancelAnimationFrame(frame);
        state = 'zero';
        group.classList.remove('is-in');
        items.forEach((item) => paint(item, 0, false));
      };
      const finish = () => {
        window.cancelAnimationFrame(frame);
        state = 'final';
        group.classList.add('is-in');
        items.forEach((item) => paint(item, 1, true));
      };
      const run = () => {
        if (state === 'running') return;
        if (state !== 'zero') reset();
        window.cancelAnimationFrame(frame);
        state = 'running';
        reserve();
        group.classList.add('is-in');
        let startedAt = 0;
        const step = (now: number) => {
          if (!startedAt) startedAt = now;
          let done = true;
          items.forEach((item, index) => {
            const progress = Math.min(1, Math.max(0, (now - startedAt - index * STAGGER) / DURATION));
            const eased = progress >= 1 ? 1 : 1 - 2 ** (-10 * progress);
            if (progress < 1) done = false;
            paint(item, eased, progress >= 1);
          });
          if (done) {
            state = 'done';
            return;
          }
          frame = window.requestAnimationFrame(step);
        };
        frame = window.requestAnimationFrame(step);
      };

      reserve();
      const onFontsOrResize = () => {
        if (state !== 'running') reserve();
      };
      document.fonts?.ready.then(onFontsOrResize).catch(() => undefined);
      window.addEventListener('resize', onFontsOrResize);
      group.classList.add('is-armed', 'is-in');

      if (typeof IntersectionObserver !== 'function') {
        run();
        teardowns.push(() => window.removeEventListener('resize', onFontsOrResize));
        return;
      }
      /* Two observers: a slightly larger "approach" zone re-arms the group at 0 just before it can be seen
         (and restores the finals once well out of sight); the visible one runs it. */
      const approach = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              if (!inView && state !== 'running' && state !== 'zero') reset();
            } else finish();
          });
        },
        { rootMargin: '12% 0px 12% 0px', threshold: 0 }
      );
      const visible = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            inView = entry.isIntersecting && entry.intersectionRatio >= 0.2;
            if (inView) run();
          });
        },
        { threshold: [0, 0.2] }
      );
      approach.observe(group);
      visible.observe(group);
      teardowns.push(() => {
        approach.disconnect();
        visible.disconnect();
        window.cancelAnimationFrame(frame);
        window.removeEventListener('resize', onFontsOrResize);
        items.forEach((item) => {
          item.el.textContent = item.final;
          item.box.style.width = '';
          item.box.style.transform = '';
        });
      });
    });

    return () => teardowns.forEach((teardown) => teardown());
  }, [rootRef, locale]);
}
